import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import classNames from 'classnames';

import lang from '../../lib/language';
import { PositionType } from '../../lib/store/reporting/types';
import { NullableString } from '../../lib/util/types';

import { UI_ROUTES } from '../../util/routes';
import { ClickableDiv, CompanyLogo } from '../index';
import ButtonsCancelAndModify from '../TableView/tableContainer/ButtonsCancelAndModify/ButtonsCancelAndModify';
import ButtonsSellAndBuy from '../TableView/tableContainer/ButtonsSellAndBuy/ButtonsSellAndBuy';

import styles from './MyAccountButtons.module.scss';

type Props = {
    data: any;
    companyLogoAndNameData: any
    hasSymbol?: boolean;
    companyLogos?: boolean;
    pendingOrders?: boolean;
    selectedPieChartElement?: { colors: string[], symbolName: string };
}

const DEFAULT_NAME_LENGTH = 16;

const MyAccountButtons = ({
  data, hasSymbol, pendingOrders, selectedPieChartElement, companyLogoAndNameData, companyLogos,
}: Props) => {
  const history = useHistory();
  const [selectedRowKey, setSelectedRowKey] = useState<NullableString>();
  const [swipedRow, setSwipedRow] = useState<number | undefined>(undefined);

  let touchStartX = 0;
  let touchEndX = 0;

  const handleTouchStart = (e) => {
    touchStartX = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX = e.touches[0].clientX;
  };

  const handleTouchEnd = (index: number) => {
    if (touchStartX - touchEndX > 50) {
      setSwipedRow(index);
    } else if (touchEndX - touchStartX > 50) {
      setSwipedRow(undefined);
    }
  };

  useEffect(() => {
    if (selectedPieChartElement) {
      setSelectedRowKey(selectedPieChartElement.symbolName);
    }
  }, [selectedPieChartElement]);

  const handleClickRowMob = (symbolName: string, e: Event): void => {
    e.preventDefault();
    history.push(`${UI_ROUTES.symbol}/${symbolName}`);
  };

  const trimName = (name: string) => {
    if (!name) {
      return '';
    }

    if (name.length > DEFAULT_NAME_LENGTH) {
      return `${name.slice(0, DEFAULT_NAME_LENGTH)}...`;
    }
    return name;
  };

  return (

    <div>
      {data.map((row, index: number) => {
        let companyLogoAndName;
        if (!hasSymbol) {
          companyLogoAndName = companyLogoAndNameData[row.name];
        }

        const rowKey = row.id;
        const activeRowStyle = classNames(
          styles.row,
          styles.rowActive,
        );

        return (
          <div
            key={row.id}
            className={classNames(styles.wrapperRow, {
              [styles.swipedLeft]: swipedRow === index,
              [activeRowStyle]: selectedRowKey === rowKey,
            })}
          >
            <div
              className={classNames(styles.row, {
                [styles.rowSwipped]: pendingOrders && swipedRow === index,
                [styles.rowBuySellSwipped]: !pendingOrders && swipedRow === index,
              })}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={() => handleTouchEnd(index)}
            >
              <ClickableDiv
                className={styles.rowClickable}
                onClick={(e) => handleClickRowMob(row.name, e)}
              >
                {
                  selectedPieChartElement && (
                    <div
                      style={{ background: selectedPieChartElement.colors[index] }}
                      className={styles.colorVerticalLine}
                    />
                  )
                }
                <div className={styles.logo}>
                  {
                    companyLogos
                      ? (
                        <CompanyLogo
                          url={companyLogoAndName?.url}
                          logo={companyLogoAndName?.logo}
                          className={styles.image}
                          symbol={row.name}
                        />
                      )
                      : <img src={row.icon} className={styles.image} alt="" />
                  }
                </div>
                <div className={styles.infoWrapper}>
                  <div className={styles.companyStocksAndPrice}>
                    <div className={styles.stocks}>
                      {pendingOrders ? (
                        <>
                          {[row.totalQuantity, row.name, 'x', row.setPrice].join(' ')}
                        </>
                      ) : (
                        <>
                          {[row.quantity, row.name, 'x', row.purchasePrice].join(' ')}
                          {row.positionType === PositionType.Sell
                            && <span className={styles.positionType}>{` ${lang.commonShort()}`}</span>}
                        </>
                      )}
                    </div>
                    <div className={styles.expiryDate}>
                      {pendingOrders ? row.expiryDate : row.marketPrice}
                    </div>
                  </div>
                  <div className={styles.companyNameAndUnrealizedProfit}>
                    <div className={styles.companyName}>
                      {pendingOrders ? row.type : trimName(row.subtitle)}
                    </div>
                    <div
                      className={classNames(styles.unrealizedProfit, {
                        [styles.positive]: !pendingOrders && row.unrealizedProfitNumber > 0,
                        [styles.negative]: !pendingOrders && row.unrealizedProfitNumber < 0,
                      })}
                    >
                      {pendingOrders ? row.quantity ?? '' : row.unrealizedProfit}
                    </div>
                  </div>
                </div>
              </ClickableDiv>
              {pendingOrders
                ? <ButtonsCancelAndModify row={row} isSwipeable />
                : <ButtonsSellAndBuy row={row} isSwipeable />}
            </div>
          </div>
        );
      })}
    </div>
  );
};
export default MyAccountButtons;
